<script lang="ts">
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { fade } from "svelte/transition";
  import type { Action } from "svelte/action";
  import Loader from "../Loader/Loader.svelte";

  export let id: string = "";
  // InputField type
  export let type: string = "text";

  // InputField name
  export let name: string = "name";

  // InputField placeholder
  export let placeholder: string = "";

  // InputField value
  export let value: string = "";

  // InputField theme
  export let theme: string = "light";

  // InputField required
  export let required: boolean = false;

  // InputField label
  export let label: string = "";

  export let error: boolean = false;
  export let errorMessage: string = "Invalid input";

  export let helperText: string = "";

  export let fullWidth: boolean = false;

  export let inlineSubmitBtn = false;

  export let loading = false;

  let fullWidthClass = fullWidth ? "full-width" : "";

  export let tabindex = 0;

  export let autocomplete = "on";

  let showPassword = false;

  export let action: Action = () => {};

  function handleShowPassword() {
    showPassword = !showPassword;
  }
</script>

<style lang="scss">
  .input-field {
    font-family: "Proxima Nova", sans-serif;
    padding: 10.5px 20px;
    border: 1px solid $outline;
    font-size: 16px;
    line-height: 22px;
    min-width: 270px;
    border-radius: 24px;

    &--wrapper {
      position: relative;
    }

    &--container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      &.full-width {
        .input-field,
        .input-field--wrapper {
          width: 100%;
        }
      }
    }

    &__submit {
      border: none;
      background: none;
      margin: 0;
      padding: 0;
      position: absolute;
      transform: translateY(-50%);
      top: 50%;
      right: 16px;
    }

    &--light {
      background-color: $surface-alt;
      color: $on-surface;

      &::placeholder {
        color: $on-surface-30;
      }

      &:focus {
        border-color: $outline;
        outline: none;
      }
    }

    &--dark {
      color: $on-surface;
      background-color: transparent;
      border: none;

      &::placeholder {
        color: $on-surface-30;
      }

      &:focus {
        outline: none;
      }
    }

    &--search {
      &__wrapper {
        transition: opacity 0.25s ease-in-out;
        opacity: 0.4;
        &:active,
        &:focus,
        &:focus-within,
        &:hover {
          opacity: 1;
        }
      }
      padding: 10.5px 34px;
      background: $background;
      min-width: 0px;
      color: $on-background;
      transition: border 0.25s ease-in-out;

      &::placeholder {
        color: $on-surface-30;
      }

      &-icon {
        position: absolute;
        transform: translateY(-50%);
        top: 50%;
        pointer-events: none;
      }

      &-loading {
        position: absolute;
        transform: translateY(-50%);
        top: 90%;
        pointer-events: none;
        right: 0;
      }

      &:focus {
        border-color: $on-surface-30;
      }
    }

    &--error {
      border-color: $feedback1;

      &:focus {
        border-color: $feedback1;
      }
    }

    &--error-message,
    &--helper-text {
      padding: 8px 20px 8px 0;
    }

    &--error-icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 20px;
      pointer-events: none;
    }
  }
  textarea.input-field {
    resize: vertical;
  }
  label {
    visibility: hidden;
    position: absolute;
  }

  input[type="date"]:not(:focus):not(.has-value).has-placeholder:before {
    color: $on-surface-30;
    content: attr(placeholder);
    position: absolute;
  }
  input[type="date"]:not(:focus):not(
      .has-value
    ).has-placeholder::-webkit-datetime-edit {
    color: transparent;
    // position: absolute;
    display: flex;
    flex-direction: row;
  }
</style>

<div class={`input-field--container ${fullWidthClass}`}>
  <div class="input-field--wrapper">
    {#if label}
      <label for={name}><p>{label}</p></label>
    {/if}
    {#if type === "text"}
      <input
        id={id ? id : name}
        class={`input-field input-field--${theme} ${
          error ? "input-field--error" : ""
        }`}
        type="text"
        {name}
        {placeholder}
        {required}
        {autocomplete}
        {tabindex}
        aria-label={type}
        aria-required={required ? true : false}
        bind:value
        use:action
        on:keydown
      />
    {:else if type === "email"}
      <input
        id={id ? id : name}
        class={`input-field input-field--${theme} ${
          error ? "input-field--error" : ""
        }`}
        type="email"
        {name}
        {placeholder}
        {required}
        aria-label={type}
        aria-required={required ? true : false}
        bind:value
      />
      {#if inlineSubmitBtn && value.length > 0}
        <button
          class="input-field__submit"
          type="submit"
          transition:fade={{ duration: 250 }}
        >
          <Icon icon="east" color="var(--on-surface)" />
        </button>
      {/if}
    {:else if type === "date"}
      <input
        id={id ? id : name}
        class={`input-field input-field--${theme} ${
          error ? "input-field--error" : ""
        }${value ? " has-value" : ""}${placeholder ? " has-placeholder" : ""}`}
        type="date"
        {name}
        {placeholder}
        {required}
        aria-label={type}
        aria-required={required ? true : false}
        bind:value
      />
    {:else if type === "password"}
      {#if showPassword}
        <input
          id={id ? id : name}
          class={`input-field input-field--${theme} ${
            error ? "input-field--error" : ""
          }`}
          type="text"
          {name}
          {placeholder}
          {required}
          aria-label={type}
          aria-required={required ? true : false}
          bind:value
        />
      {:else}
        <input
          id={id ? id : name}
          class={`input-field input-field--${theme} ${
            error ? "input-field--error" : ""
          }`}
          type="password"
          {name}
          {placeholder}
          {required}
          aria-label={type}
          aria-required={required ? true : false}
          bind:value
        />
      {/if}

      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <span
        role="button"
        tabindex="0"
        class="input-field__submit"
        on:click|preventDefault={handleShowPassword}
        transition:fade={{ duration: 250 }}
      >
        <Icon
          icon={showPassword ? "show" : "hide"}
          color="var(--on-surface)"
          customIcon={true}
        />
      </span>
    {:else if type === "textarea"}
      <textarea
        class={`input-field input-field--${theme} ${
          error ? "input-field--error" : ""
        }`}
        {name}
        {placeholder}
        {required}
        aria-label={type}
        aria-required={required ? true : false}
        bind:value
      />
    {:else if type === "search"}
      <div class="input-field--search__wrapper">
        <span class="input-field--search-icon">
          <Icon
            icon="search"
            color={theme === "light"
              ? "var(--on-surface-30)"
              : "var(--on-surface)"}
          />
        </span>

        {#if value.length > 0}
          {#if loading}
            <span class="input-field--search-loading">
              <Loader width={24} height={24} />
            </span>
          {:else}
            <button
              class="input-field__submit"
              type="submit"
              transition:fade={{ duration: 250 }}
            >
              <Icon
                icon="chevron_right"
                color={theme === "light"
                  ? "var(--on-surface-30)"
                  : "var(--on-surface)"}
              />
            </button>
          {/if}
        {/if}
        <!-- svelte-ignore a11y-autofocus -->
        <input
          id={id ? id : name}
          class={`input-field input-field--search input-field--${theme}`}
          type="text"
          {name}
          {placeholder}
          {autocomplete}
          {required}
          autofocus
          aria-label={type}
          aria-required={required ? true : false}
          bind:value
          on:focus
          on:mouseover
          on:blur
        />
      </div>
    {/if}
    {#if error && value.length <= 0}
      <span class="input-field--error-icon" transition:fade={{ duration: 250 }}>
        <Icon icon="warning" customIcon />
      </span>
    {/if}
  </div>
  {#if error}
    <div class="input-field--error-message">
      <Typography component="span" variant="caption2" theme="feedback1">
        {@html errorMessage}
      </Typography>
    </div>
  {/if}
  {#if helperText}
    <div class="input-field--helper-text">
      <Typography component="span" variant="caption2">
        {helperText}
      </Typography>
    </div>
  {/if}
</div>
